// i18n.js — lightweight internationalization.
//
// Each locale is a flat JSON file in src/data/locales/ imported below.
// Missing keys fall back to English, so a new locale file only needs
// to supply the strings it wants to override. Add matching endonyms
// to LOCALE_DISPLAY_NAMES so the language picker shows the language
// in its own script.

import enLocale from './data/locales/en.json';
import esLocale from './data/locales/es.json';
import deLocale from './data/locales/de.json';
import jaLocale from './data/locales/ja.json';
import koLocale from './data/locales/ko.json';
import ukLocale from './data/locales/uk.json';
import zhHansLocale from './data/locales/zh-Hans.json';

const LOCALES = Object.freeze({
  en: enLocale,
  es: esLocale,
  de: deLocale,
  ja: jaLocale,
  ko: koLocale,
  uk: ukLocale,
  'zh-Hans': zhHansLocale,
});

/**
 * Display names for each registered locale, written in the locale's
 * OWN language (endonym). The settings panel's language picker uses
 * these so a native English speaker sees "English" and a native
 * Spanish speaker would see "Español" — never "EN" / "ES".
 *
 * Endonyms are deliberately preferred over the UI-locale's exonyms
 * because a parent who needs to switch the UI to a language they can
 * read shouldn't first have to recognize the current locale's name
 * for it. Add new entries here whenever LOCALES grows.
 */
const LOCALE_DISPLAY_NAMES = Object.freeze({
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  ja: '日本語',
  ko: '한국어',
  uk: 'Українська',
  'zh-Hans': '简体中文',
});

const DEFAULT_LOCALE = 'en';

/**
 * Region-based BCP 47 tags that map to our script-based locale codes.
 * Browsers report Simplified Chinese as "zh-CN" (region-based), but our
 * locale key is "zh-Hans" (script-based). Without this mapping,
 * navigator.languages = ['zh-CN'] would fail to match. Traditional
 * Chinese (zh-TW, zh-HK) is intentionally absent — we don't have that
 * locale yet.
 */
const REGION_TO_LOCALE = Object.freeze({
  'zh-CN': 'zh-Hans',
  'zh-SG': 'zh-Hans',
});

let currentLocale = DEFAULT_LOCALE;

/** Return the currently active locale code. */
export function getLocale() {
  return currentLocale;
}

/**
 * Set the active locale. Unknown locale codes are rejected and the
 * current locale is left unchanged.
 *
 * @param {string} locale
 * @returns {boolean} True if the locale changed, false otherwise.
 */
export function setLocale(locale) {
  if (!(locale in LOCALES)) {
    return false;
  }
  if (locale === currentLocale) {
    return false;
  }
  currentLocale = locale;
  return true;
}

/**
 * Detect the best supported locale from the browser's language
 * preferences. Walks `navigator.languages` (falling back to
 * `navigator.language`) and returns the first match against our
 * registered locales.
 *
 * Matching order for each browser language tag:
 *   1. Exact match           — "es" → "es"
 *   2. Region→script mapping — "zh-CN" → "zh-Hans"
 *   3. Prefix match          — "zh-Hans-CN" starts with "zh-Hans" → "zh-Hans"
 *   4. Base-language match   — "es-MX" → base "es" → "es"
 *
 * Returns DEFAULT_LOCALE ('en') if nothing matches.
 *
 * @returns {string}
 */
export function detectBrowserLocale() {
  const languages =
    (typeof navigator !== 'undefined' && navigator.languages?.length)
      ? navigator.languages
      : (typeof navigator !== 'undefined' && navigator.language)
        ? [navigator.language]
        : [];

  const available = Object.keys(LOCALES);

  for (const lang of languages) {
    // 1. Exact match — "es", "en", "zh-Hans"
    if (lang in LOCALES) return lang;

    // 2. Explicit region-to-script mapping — "zh-CN" → "zh-Hans"
    if (lang in REGION_TO_LOCALE) return REGION_TO_LOCALE[lang];

    // 3. Browser tag is more specific than our key — "zh-Hans-CN"
    //    starts with "zh-Hans"
    for (const code of available) {
      if (lang.startsWith(code + '-')) return code;
    }

    // 4. Base language match — "es-MX" → "es"
    const base = lang.split('-')[0];
    if (base in LOCALES) return base;
  }

  return DEFAULT_LOCALE;
}

/** Return the list of registered locale codes. */
export function getAvailableLocales() {
  return Object.keys(LOCALES);
}

/**
 * Look up the human-readable display name (endonym) for a locale code.
 * Falls back to the uppercased code if the locale isn't registered in
 * LOCALE_DISPLAY_NAMES — that fallback only fires for typos or for
 * locales added to LOCALES without a matching display-name entry, so
 * it's a debug aid rather than a user-facing path.
 *
 * @param {string} locale
 * @returns {string}
 */
export function getLocaleDisplayName(locale) {
  return LOCALE_DISPLAY_NAMES[locale] ?? locale.toUpperCase();
}

/**
 * Look up a translation by key. Missing keys fall back to English, then
 * to the key itself — this keeps the UI usable even if a locale file is
 * incomplete.
 *
 * @param {string} key
 * @returns {string}
 */
export function t(key) {
  const active = LOCALES[currentLocale];
  if (active && key in active) return active[key];
  const fallback = LOCALES[DEFAULT_LOCALE];
  if (fallback && key in fallback) return fallback[key];
  return key;
}

/** Test-only: reset to the default locale. */
export function __resetForTests() {
  currentLocale = DEFAULT_LOCALE;
}
